// -----------------------------------------------------------------------------
// Probate.jsx — canonical page for /texas-probate-attorney/
//
// Statewide Texas Probate authority hub. Independent administration, dependent
// administration, muniment of title, small estate affidavits, heirship
// determinations. Editorial pattern matches the five other authority pages
// (APA, Succession, Trademark, Physician, Estate Planning).
// -----------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";
import { breadcrumbSchema, faqPageSchema, legalServiceSchema, attorneySchema } from "@/lib/schemas";
import { trackAuthorityPageView, trackPriorityFaqOpen } from "@/lib/analytics";
import { ChevronDown, Phone, Star } from "lucide-react";

const PHONE = "(972) 712-1515";
const PHONE_TEL = "9727121515";
const CANONICAL = "/texas-probate-attorney/";

const IN_PAGE_NAV = [
  ["What Probate Is", "what-probate-is"],
  ["Types", "types"],
  ["When It's Needed", "when-needed"],
  ["FAQs", "faqs"],
  ["Related", "related"],
  ["Contact", "contact"],
];

// Types of Texas probate administration — the working menu.
const PROBATE_TYPES = [
  ["Independent Administration", "The most common and least court-supervised form of Texas probate. Available when the will authorizes it, or when all distributees agree, and the estate is not encumbered by unusual circumstances. The independent executor administers the estate without ongoing court oversight after the initial inventory or affidavit is filed."],
  ["Dependent Administration", "Court-supervised administration used when independent administration is not available or not appropriate. Every sale, distribution, and material action is subject to court approval. More expensive and slower; sometimes the correct choice for contested estates or when the personal representative cannot obtain bond and letters otherwise."],
  ["Muniment of Title", "A streamlined Texas probate proceeding that is available in defined circumstances when the estate has a valid will and no unpaid debts (other than debts secured by real property). The will is admitted to probate as a muniment of title without appointing a personal representative \u2014 often the fastest and least expensive path when it fits."],
  ["Small Estate Affidavit", "A statutory affidavit procedure available for small estates that meet strict statutory criteria \u2014 total value below the threshold, no will, and defined creditor and heirship conditions. Not appropriate for real property except the homestead. Fact-specific eligibility."],
  ["Heirship Determination", "A judicial proceeding to determine the heirs of a person who died without a will (intestate) or when the will does not dispose of all assets. Frequently required to clear title to Texas real estate or to open a bank account of the deceased."],
  ["Ancillary Probate", "The Texas administration of a non-resident decedent's Texas property, coordinated with the primary probate proceeding in the state of the decedent's domicile. Common when Texas real estate is owned by an out-of-state estate."],
];

// Situations where probate may (or may not) be needed.
const WHEN_NEEDED = [
  ["Probate is generally required", "When Texas real estate, brokerage accounts, or bank accounts are titled solely in the decedent's individual name without a beneficiary designation or transfer-on-death registration. Title cannot cleanly pass to heirs or beneficiaries without a probate proceeding."],
  ["Probate is generally not required", "For assets that pass by beneficiary designation (life insurance, retirement accounts, transfer-on-death accounts), assets titled to a properly funded revocable trust, jointly owned real estate with a right of survivorship, or homestead in specific circumstances defined by statute."],
  ["Small estate exception", "When the total probate estate is modest and defined statutory conditions are met, a small estate affidavit may substitute for a full probate proceeding."],
  ["Real-estate-only exception", "When there is a valid will, no unpaid debts (other than a debt secured by real property), and the primary need is to clear title, a muniment-of-title proceeding may be the right instrument."],
  ["Contested estates", "Family disputes, creditor claims, will contests, or ambiguity in the will can push an estate that would otherwise qualify for independent administration into dependent administration \u2014 more supervised, more expensive, but sometimes necessary."],
  ["Out-of-state decedent, Texas property", "An estate opened in another state generally requires ancillary probate in Texas to reach Texas assets. Coordinated with domicile counsel."],
];

// The eight curated priority FAQs (attorney-drafted, calm, no guarantees).
const PRIORITY_FAQ = [
  ["What is probate in Texas?",
   "Probate is the court-supervised process of proving a will (if one exists), appointing a personal representative, identifying and inventorying the estate's assets, addressing debts and taxes, and distributing the remaining property to the beneficiaries or heirs. Texas offers multiple forms of probate \u2014 independent administration, dependent administration, muniment of title, small estate affidavit, and heirship determination \u2014 with different levels of court supervision, cost, and speed."],
  ["How long does probate take in Texas?",
   "Timelines depend on the type of proceeding, the complexity of the estate, whether the will is contested, and the court's docket. An independent administration of a straightforward estate can move through the initial phases in a few months, with distributions occurring as debts and taxes are resolved. Dependent administrations and contested estates commonly take 12 to 24 months or more. Every estate has its own timeline; general averages should not be relied upon as an estimate for a specific matter."],
  ["Do all estates go through probate?",
   "No. Assets that pass by beneficiary designation (life insurance, retirement accounts, transfer-on-death registrations), assets titled to a properly funded revocable trust, joint tenancy with rights of survivorship, and certain homestead property can pass outside probate. Whether probate is required depends on how the specific assets are titled and what non-probate transfer instruments are in place \u2014 not on the size of the estate alone."],
  ["What is independent administration?",
   "Independent administration is the streamlined form of Texas probate in which the personal representative administers the estate without ongoing court oversight after the initial filings. It is available when the will authorizes it or when all distributees agree. It is significantly less expensive and faster than dependent administration and is the default for most Texas estates that qualify."],
  ["What is muniment of title?",
   "Muniment of title is a specialized Texas probate proceeding available when there is a valid will, no unpaid debts other than debts secured by real property, and no need to appoint a personal representative for other purposes. The will is admitted to probate as a muniment of title \u2014 the document that transfers title \u2014 without a full administration. When it fits the circumstances, it is often the fastest and least expensive probate path."],
  ["What is a small estate affidavit?",
   "A small estate affidavit is a statutory Texas procedure that permits the transfer of a decedent's assets without a full probate administration, provided the estate meets defined criteria: total value below the statutory threshold (as adjusted by law), no will, defined creditor conditions, and identified heirs. It cannot be used for real property except the homestead. Eligibility is fact-specific; a small estate affidavit is not a substitute for probate in every low-value estate."],
  ["What happens if there is no will?",
   "When a Texan dies without a will (intestate), the estate is distributed under Texas intestacy law to a defined class of heirs \u2014 typically the surviving spouse, children, and, in their absence, more distant relatives \u2014 in shares set by statute. A judicial heirship determination is frequently required to establish the heirs, and an administration may be needed to transfer title to Texas real estate or to open financial accounts. Intestate outcomes are frequently different from what the family expected \u2014 a basic will is nearly always better than intestacy."],
  ["How much does probate cost?",
   "Costs vary widely with the type of proceeding, the complexity of the estate, the court, and whether the matter is contested. An independent administration of a straightforward estate is meaningfully less expensive than a dependent administration, and a muniment-of-title proceeding is typically less than either. Attorney's fees, court costs, publication and citation, bond (if required), appraisal, and tax preparation are the common categories. Every estate is quoted individually; no publicly listed schedule is meaningful for a specific matter."],
];

// Contextual cross-links to related authority hubs.
const CONTEXTUAL_LINKS = [
  { label: "Estate Planning", desc: "Coordinated wills, trusts, powers of attorney, and beneficiary designations \u2014 the plan that determines whether and how probate is needed.", href: "/texas-estate-planning-attorney/" },
  { label: "Trusts", desc: "Revocable, irrevocable, special-needs, and business-owner trust planning \u2014 including trusts that can help minimize the need for probate.", href: "/trusts" },
  { label: "Trust Administration", desc: "Post-death trust administration for successor trustees \u2014 notices, accountings, distributions, and fiduciary-risk management.", href: "/trusts/trust-administration" },
  { label: "Business Succession", desc: "When a decedent held business interests, probate is coordinated with the company agreement, buy-sell, and succession plan.", href: "/texas-business-succession-planning-attorney/" },
];

function FaqItem({ q, a, i, onOpen }) {
  const [open, setOpen] = useState(i === 0);
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && onOpen) onOpen();
      return next;
    });
  };
  return (
    <div className="hairline-top py-6" data-testid={`probate-faq-${i}`}>
      <button
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex justify-between items-start gap-6 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass"
      >
        <h3 className="font-display text-xl md:text-2xl text-navy leading-tight">{q}</h3>
        <ChevronDown
          size={22}
          className={`shrink-0 text-brass transition-transform mt-1 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className={`${open ? "block" : "hidden"} mt-4 text-slate2 font-serif text-base leading-relaxed`}>
        {a}
      </div>
    </div>
  );
}

export default function ProbateFlagship() {
  useEffect(() => {
    trackAuthorityPageView({
      page_slug: "texas-probate-attorney",
      page_title: "Texas Probate Attorney",
      priority_faq_count: PRIORITY_FAQ.length,
    });
  }, []);

  return (
    <div className="bg-ivory" data-testid="probate-page">
      <SEO
        title="Texas Probate Attorney | Independent Administration & Estate Administration | Lone Star Counsel"
        description="Texas probate counsel for families, executors, and beneficiaries: independent administration, dependent administration, muniment of title, small estate affidavits, and heirship determinations."
        keywords="Texas Probate Attorney, Texas independent administration, muniment of title Texas, small estate affidavit Texas, heirship determination Texas, dependent administration Texas, ancillary probate Texas, Texas estate administration attorney"
        path={CANONICAL}
        schema={[
          legalServiceSchema({
            name: "Texas Probate Attorney",
            description: "Counsel for Texas probate and estate administration \u2014 independent administration, dependent administration, muniment of title, small estate affidavits, and heirship determinations.",
            url: `https://lonestarcounsel.com${CANONICAL}`,
          }),
          attorneySchema(),
          faqPageSchema(PRIORITY_FAQ.map(([q, a]) => ({ q, a }))),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Texas Probate Attorney", path: CANONICAL },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="bg-ivory" data-testid="probate-hero">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-8">
            <div className="kicker">Lone Star &#9733; Counsel</div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-navy">
              Texas Probate<br />
              <span className="italic text-oxblood">Attorney.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate2 font-serif leading-relaxed" data-testid="probate-direct-answer">
              Lone Star Counsel represents Texas families, executors, and beneficiaries through
              the probate court &mdash; from a straightforward independent administration or a
              muniment of title, to a contested administration, an heirship determination, or an
              ancillary proceeding involving out-of-state property. The right path is often the
              least court-supervised path that fits the estate. The purpose is to transfer the
              estate lawfully, resolve creditors and taxes, and complete the administration with
              as little cost, delay, and conflict as the circumstances allow.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link to="/intake" className="btn-primary" data-testid="probate-hero-cta-primary">
                Schedule a Probate Conference
              </Link>
              <a
                href={`tel:${PHONE_TEL}`}
                className="text-[11px] uppercase tracking-[0.32em] text-navy hover:text-oxblood transition-colors font-medium border-b border-brass/50 hover:border-oxblood pb-1"
                data-testid="probate-hero-cta-phone"
              >
                <Phone size={11} className="inline mr-2" />
                or call {PHONE}
              </a>
            </div>
            <div className="mt-8 max-w-2xl">
              <AttorneyReviewNote reviewed="February 2026" compact />
            </div>
          </div>
          <aside className="lg:col-span-4">
            <nav aria-label="On this page" className="sticky top-24 border-l border-brass/40 pl-6" data-testid="probate-in-page-nav">
              <div className="kicker">On this page</div>
              <ul className="mt-4 space-y-2 text-sm font-serif">
                {IN_PAGE_NAV.map(([label, id]) => (
                  <li key={id}>
                    <a href={`#${id}`} className="text-navy/70 hover:text-oxblood transition-colors">
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        </div>
      </section>

      {/* WHAT PROBATE IS */}
      <section id="what-probate-is" className="bg-linen hairline-top py-20 md:py-28" data-testid="probate-what">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="kicker">Direct answer</div>
              <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
                What Probate <span className="italic text-oxblood">Is.</span>
              </h2>
              <p className="mt-6 text-navy font-serif leading-relaxed">
                Probate is the court-supervised process of transferring a deceased Texan's
                property to the beneficiaries under a will or to the heirs under intestacy law.
                A properly designed estate plan can reduce, streamline, or in some cases avoid
                probate entirely &mdash; but when probate is required, the goal is to complete
                it in the least court-supervised, least expensive, least contested form that
                the specific estate can support.
              </p>
              <p className="mt-4 text-navy/85 font-cormorant italic text-lg leading-snug border-l-2 border-brass pl-4">
                The right probate is the one that fits the estate &mdash; not the one that is
                fashionable, and not the one that a form-driven approach would generate by
                default.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="kicker">What we handle</div>
              <ul className="mt-6 space-y-3">
                <li className="hairline-top pt-4 flex gap-3">
                  <Star size={10} className="text-brass fill-brass mt-2 shrink-0" aria-hidden />
                  <span className="text-navy/85 leading-relaxed">Filing the application and admitting the will (if any) to probate.</span>
                </li>
                <li className="hairline-top pt-4 flex gap-3">
                  <Star size={10} className="text-brass fill-brass mt-2 shrink-0" aria-hidden />
                  <span className="text-navy/85 leading-relaxed">Qualifying the personal representative and issuing letters testamentary or of administration.</span>
                </li>
                <li className="hairline-top pt-4 flex gap-3">
                  <Star size={10} className="text-brass fill-brass mt-2 shrink-0" aria-hidden />
                  <span className="text-navy/85 leading-relaxed">Inventory, appraisement, and list of claims &mdash; or the affidavit in lieu, where applicable.</span>
                </li>
                <li className="hairline-top pt-4 flex gap-3">
                  <Star size={10} className="text-brass fill-brass mt-2 shrink-0" aria-hidden />
                  <span className="text-navy/85 leading-relaxed">Notice to creditors and beneficiaries; resolution of claims, debts, and taxes.</span>
                </li>
                <li className="hairline-top pt-4 flex gap-3">
                  <Star size={10} className="text-brass fill-brass mt-2 shrink-0" aria-hidden />
                  <span className="text-navy/85 leading-relaxed">Sale, retitling, and distribution of real property, brokerage, retirement, and business interests.</span>
                </li>
                <li className="hairline-top pt-4 flex gap-3">
                  <Star size={10} className="text-brass fill-brass mt-2 shrink-0" aria-hidden />
                  <span className="text-navy/85 leading-relaxed">Coordination with any trust, buy-sell, and business-succession documents.</span>
                </li>
                <li className="hairline-top pt-4 flex gap-3">
                  <Star size={10} className="text-brass fill-brass mt-2 shrink-0" aria-hidden />
                  <span className="text-navy/85 leading-relaxed">Contested proceedings &mdash; will contests, capacity challenges, and executor disputes &mdash; when needed.</span>
                </li>
                <li className="hairline-top pt-4 flex gap-3">
                  <Star size={10} className="text-brass fill-brass mt-2 shrink-0" aria-hidden />
                  <span className="text-navy/85 leading-relaxed">Final accounting and closing of the administration.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TYPES OF PROBATE */}
      <section id="types" className="bg-ivory py-20 md:py-28" data-testid="probate-types">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Six paths through the court</div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
            Types of Texas <span className="italic text-oxblood">Probate Administration.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            Texas offers multiple probate forms with materially different levels of court
            supervision, cost, and speed. Selecting the right form for the specific estate is
            often the difference between a well-run administration and an unnecessarily
            expensive one.
          </p>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2" data-testid="probate-types-list">
            {PROBATE_TYPES.map(([title, desc], i) => (
              <li key={i} className="py-4 hairline-top" data-testid={`probate-type-${i}`}>
                <div className="flex items-baseline gap-3">
                  <Star size={9} className="text-brass fill-brass" aria-hidden />
                  <h3 className="font-display text-lg text-navy">{title}</h3>
                </div>
                <p className="mt-2 pl-6 text-sm text-navy/75 leading-relaxed font-serif">{desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* UNCLAIMED PROPERTY CALLOUT — link from main probate hub to the
          secondary unclaimed-property service page. Proportionate visibility. */}
      <section className="bg-linen hairline-top py-14 md:py-16" data-testid="probate-unclaimed-callout">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                Related service
              </div>
              <h3 className="mt-3 font-display text-xl md:text-2xl text-navy leading-snug">
                Found a deceased relative&rsquo;s property on <span className="italic text-oxblood">ClaimItTexas.gov</span>?
              </h3>
              <p className="mt-2 text-sm text-navy/75 font-serif leading-relaxed max-w-2xl">
                We help Texas families determine the appropriate legal path when the Texas
                Comptroller requires proof of inheritance before releasing unclaimed property
                to a deceased owner&rsquo;s family &mdash; whether through existing probate
                documents, a qualifying claim, muniment of title, or a determination of heirship.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/texas-unclaimed-property-probate"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-oxblood hover:text-navy transition-colors font-medium border-b border-oxblood/40 hover:border-navy pb-1"
                data-testid="probate-unclaimed-link"
              >
                Unclaimed Property Claims &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHEN NEEDED */}
      <section id="when-needed" className="bg-linen hairline-top py-20 md:py-28" data-testid="probate-when-needed">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Fact-specific</div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
            When Probate <span className="italic text-oxblood">Is (and Isn&apos;t) Needed.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            Whether probate is required depends on how the specific assets are titled, what
            non-probate transfer instruments (beneficiary designations, transfer-on-death
            registrations, funded trusts) are in place, and whether the estate meets defined
            statutory exceptions &mdash; not on the size of the estate alone.
          </p>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2" data-testid="probate-when-list">
            {WHEN_NEEDED.map(([title, desc], i) => (
              <li key={i} className="py-4 hairline-top" data-testid={`probate-when-${i}`}>
                <div className="flex items-baseline gap-3">
                  <span className="text-brass" aria-hidden>&#9733;</span>
                  <h3 className="font-display text-lg text-navy">{title}</h3>
                </div>
                <p className="mt-2 pl-6 text-sm text-navy/75 leading-relaxed font-serif">{desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQS */}
      <section id="faqs" className="bg-ivory py-20 md:py-28" data-testid="probate-faq">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently Asked Questions</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-navy leading-[1.02]">
            Answers <span className="italic text-oxblood">before the courthouse.</span>
          </h2>
          <p className="mt-4 max-w-3xl text-navy/75 font-serif leading-relaxed">
            The most common questions we receive from Texas families and executors, answered
            directly. General information only &mdash; not legal advice on your specific matter.
          </p>
          <div className="mt-10" data-testid="probate-priority-faq">
            {PRIORITY_FAQ.map(([q, a], i) => (
              <FaqItem
                key={i}
                q={q}
                a={a}
                i={i}
                onOpen={() =>
                  trackPriorityFaqOpen({
                    page_slug: "texas-probate-attorney",
                    faq_position: i + 1,
                    faq_question: q,
                  })
                }
              />
            ))}
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section id="related" className="bg-linen hairline-top py-20 md:py-28" data-testid="probate-related">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Related counsel</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-navy leading-[1.05]">
            Coordinate probate with{" "}
            <span className="italic text-oxblood">the surrounding plan.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            Probate is one instrument in a coordinated plan. When it fits with the surrounding
            estate, trust, and business documents, it moves quickly. When it does not, the plan
            on paper and the plan in fact fight each other.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="probate-related-list">
            {CONTEXTUAL_LINKS.map((c, i) => (
              <Link
                key={i}
                to={c.href}
                className="group hairline-top pt-5 block hover:bg-ivory/60 transition-colors -mx-3 px-3"
                data-testid={`probate-related-${i}`}
              >
                <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                  {c.label}
                </div>
                <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                  {c.desc}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section id="contact" className="panel-navy grain grain-dark py-20 md:py-28 relative overflow-hidden" data-testid="probate-cta">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <Star size={11} className="text-brass fill-brass mx-auto" aria-hidden />
          <div className="mt-8 kicker kicker-ivory">A message from the firm</div>
          <h2 className="mt-4 font-display text-ivory text-3xl md:text-5xl leading-[1.05] max-w-3xl mx-auto">
            The Right Path.{" "}
            <span className="italic font-cormorant text-brass">The First Time.</span>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-ivory/85 font-serif text-lg leading-relaxed">
            Probate is not a single procedure. It is a menu of six court-supervised paths, only
            some of which will fit any specific estate. Selecting the right path at the outset
            &mdash; and preparing the filings and inventory carefully &mdash; is what turns a
            probate into a routine matter rather than a protracted one.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
            <Link to="/intake" className="btn-brass" data-testid="probate-cta-primary">
              Schedule a Confidential Conference
            </Link>
            <a
              href={`tel:${PHONE_TEL}`}
              className="text-[11px] uppercase tracking-[0.32em] text-ivory/85 hover:text-brass transition-colors font-medium border-b border-brass/40 hover:border-brass pb-1"
              data-testid="probate-cta-phone"
            >
              <Phone size={11} className="inline mr-2" />
              or call {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-navy text-ivory/70 py-10 text-xs">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 font-serif leading-relaxed">
          <strong className="text-brass tracking-widest uppercase text-[10px]">Disclaimer.</strong>{" "}
          The information on this page is for general informational purposes only and is not
          legal, tax, accounting, or financial advice. Reading this page or contacting the firm
          does not create an attorney-client relationship. An attorney-client relationship is
          established only through a written agreement signed by the client and the firm. Past
          results do not guarantee future outcomes. Each probate matter depends on its specific
          facts and applicable law, including Texas Estates Code procedure and the court where
          the matter is filed.
          <div className="max-w-3xl">
            <AttorneyReviewNote reviewed="February 2026" variant="dark" compact />
          </div>
        </div>
      </section>
    </div>
  );
}
